import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type { IntervalRecurrente } from '@/lib/invoicing/types';

// =============================================
// Recurring Invoice Management
// POST: Mark invoice as recurrente with config
// GET: List recurring invoices due for generation
// PUT with action=generate: Generate next occurrence
// =============================================

function calculateNextDate(currentDate: string, intervalo: IntervalRecurrente): string {
  const date = new Date(currentDate);
  switch (intervalo) {
    case 'mensual':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'trimestral':
      date.setMonth(date.getMonth() + 3);
      break;
    case 'anual':
      date.setFullYear(date.getFullYear() + 1);
      break;
  }
  return date.toISOString().split('T')[0];
}

async function generateNextNumber(serieId: string): Promise<{ numero: string; nextSeq: number }> {
  const { data: serie, error } = await supabaseAdmin
    .from('invoice_series')
    .select('*')
    .eq('id', serieId)
    .single();

  if (error || !serie) {
    throw new Error('Serie no encontrada');
  }

  if (!serie.activa) {
    throw new Error('La serie no está activa');
  }

  const seq = serie.siguiente_numero ?? 1;
  const prefijo = serie.prefijo ?? '';
  const paddedNum = String(seq).padStart(5, '0');
  const numero = `${prefijo}${paddedNum}`;

  return { numero, nextSeq: seq + 1 };
}

export default withAuth(async (req, res, user) => {
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res);
      case 'POST':
        return handleSetRecurring(req, res);
      case 'PUT':
        return handleAction(req, res, user.id);
      default:
        return res.status(405).json({ error: 'Método no permitido' });
    }
  } catch (error: any) {
    console.error('[API recurring] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});

// =============================================
// GET: List recurring invoices due for generation
// =============================================

async function handleGet(req: NextApiRequest, res: NextApiResponse) {
  const { empresa_id } = req.query;

  if (!empresa_id) {
    return res.status(400).json({ error: 'empresa_id es requerido' });
  }

  const today = new Date().toISOString().split('T')[0];

  // Find recurring invoices where proximo date is today or earlier
  const { data, error } = await supabaseAdmin
    .from('invoices')
    .select('*')
    .eq('empresa_id', empresa_id as string)
    .eq('es_recurrente', true)
    .not('recurrente_config', 'is', null)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[API recurring] GET error:', error);
    return res.status(500).json({ error: `Error al listar recurrentes: ${error.message}` });
  }

  // Filter by proximo date (JSON field filtering)
  const dueInvoices = (data ?? []).filter((inv: Record<string, unknown>) => {
    const config = inv.recurrente_config as { proximo?: string; fin?: string } | null;
    if (!config?.proximo) return false;

    // Check if past end date
    if (config.fin && config.fin < today) return false;

    return config.proximo <= today;
  });

  const allRecurring = (data ?? []).filter((inv: Record<string, unknown>) => {
    const config = inv.recurrente_config as { fin?: string } | null;
    if (!config) return false;
    // Exclude expired
    if (config.fin && config.fin < today) return false;
    return true;
  });

  return res.status(200).json({
    data: allRecurring,
    due: dueInvoices,
    dueCount: dueInvoices.length,
  });
}

// =============================================
// POST: Mark invoice as recurrente with config
// =============================================

async function handleSetRecurring(req: NextApiRequest, res: NextApiResponse) {
  const { invoice_id, intervalo, proximo_fecha, fecha_fin } = req.body;

  if (!invoice_id) return res.status(400).json({ error: 'invoice_id es requerido' });
  if (!intervalo) return res.status(400).json({ error: 'intervalo es requerido' });
  if (!proximo_fecha) return res.status(400).json({ error: 'proximo_fecha es requerida' });

  const validIntervalos: IntervalRecurrente[] = ['mensual', 'trimestral', 'anual'];
  if (!validIntervalos.includes(intervalo)) {
    return res.status(400).json({ error: `intervalo inválido. Valores permitidos: ${validIntervalos.join(', ')}` });
  }

  // Verify invoice exists
  const { data: invoice, error: fetchError } = await supabaseAdmin
    .from('invoices')
    .select('id, estado')
    .eq('id', invoice_id)
    .single();

  if (fetchError || !invoice) {
    return res.status(404).json({ error: 'Factura no encontrada' });
  }

  const recurrenteConfig = {
    intervalo,
    proximo: proximo_fecha,
    fin: fecha_fin ?? null,
  };

  const { data, error } = await supabaseAdmin
    .from('invoices')
    .update({
      es_recurrente: true,
      recurrente_config: recurrenteConfig,
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoice_id)
    .select()
    .single();

  if (error) {
    console.error('[API recurring] POST error:', error);
    return res.status(500).json({ error: `Error al configurar recurrencia: ${error.message}` });
  }

  return res.status(200).json({ success: true, data });
}

// =============================================
// PUT action=generate: Generate next occurrence
// =============================================

async function handleAction(req: NextApiRequest, res: NextApiResponse, userId: string) {
  const { action, invoice_id } = req.body;

  if (action !== 'generate') {
    return res.status(400).json({ error: 'action debe ser "generate"' });
  }

  if (!invoice_id) {
    return res.status(400).json({ error: 'invoice_id es requerido' });
  }

  // Fetch parent recurring invoice
  const { data: parent, error: fetchError } = await supabaseAdmin
    .from('invoices')
    .select('*')
    .eq('id', invoice_id)
    .single();

  if (fetchError || !parent) {
    return res.status(404).json({ error: 'Factura recurrente no encontrada' });
  }

  if (!parent.es_recurrente || !parent.recurrente_config) {
    return res.status(400).json({ error: 'La factura no está configurada como recurrente' });
  }

  const config = parent.recurrente_config as {
    intervalo: IntervalRecurrente;
    proximo: string;
    fin?: string;
  };

  // Check if past end date
  const today = new Date().toISOString().split('T')[0];
  if (config.fin && config.fin < today) {
    return res.status(400).json({ error: 'La recurrencia ha finalizado (fecha fin superada)' });
  }

  // Fetch parent lines
  const { data: parentLines } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', invoice_id)
    .order('orden', { ascending: true });

  // Generate new number from same series
  let numero: string;
  let nextSeq: number;
  try {
    const result = await generateNextNumber(parent.serie_id);
    numero = result.numero;
    nextSeq = result.nextSeq;
  } catch (err: any) {
    return res.status(400).json({ error: err.message });
  }

  // Create new invoice from parent
  const newInvoice = {
    empresa_id: parent.empresa_id,
    serie_id: parent.serie_id,
    numero,
    tipo: parent.tipo,
    estado: 'borrador',
    cliente_nombre: parent.cliente_nombre,
    cliente_nif: parent.cliente_nif,
    cliente_direccion: parent.cliente_direccion,
    cliente_email: parent.cliente_email,
    cliente_telefono: parent.cliente_telefono,
    fecha_emision: config.proximo || today,
    fecha_vencimiento: null,
    fecha_operacion: null,
    moneda: parent.moneda,
    tipo_cambio: parent.tipo_cambio,
    base_imponible: parent.base_imponible,
    total_impuestos: parent.total_impuestos,
    total_retenciones: parent.total_retenciones,
    total_suplidos: parent.total_suplidos,
    total_factura: parent.total_factura,
    tipo_operacion: parent.tipo_operacion,
    clave_regimen: parent.clave_regimen,
    factura_rectificada_id: null,
    motivo_rectificacion: null,
    es_recurrente: false,
    recurrente_config: null,
    generada_desde: parent.id,
    verifactu_enviada: false,
    verifactu_id: null,
    verifactu_qr: null,
    verifactu_fecha_envio: null,
    verifactu_estado: null,
    face_enviada: false,
    face_numero_registro: null,
    face_estado: null,
    email_enviado: false,
    email_fecha_envio: null,
    pdf_url: null,
    pdf_generado: false,
    notas: parent.notas,
    notas_internas: `Generada automáticamente desde factura recurrente ${parent.numero}`,
    created_by: userId,
  };

  const { data: createdInvoice, error: insertError } = await supabaseAdmin
    .from('invoices')
    .insert(newInvoice)
    .select()
    .single();

  if (insertError) {
    console.error('[API recurring] Insert error:', insertError);
    return res.status(500).json({ error: `Error al generar factura: ${insertError.message}` });
  }

  // Copy lines
  if (parentLines && parentLines.length > 0) {
    const newLines = parentLines.map((line: Record<string, unknown>) => ({
      invoice_id: createdInvoice.id,
      orden: line.orden,
      concepto: line.concepto,
      descripcion: line.descripcion,
      cantidad: line.cantidad,
      precio_unitario: line.precio_unitario,
      descuento_porcentaje: line.descuento_porcentaje,
      base_linea: line.base_linea,
      tipo_iva: line.tipo_iva,
      porcentaje_iva: line.porcentaje_iva,
      cuota_iva: line.cuota_iva,
      recargo_porcentaje: line.recargo_porcentaje,
      recargo_cuota: line.recargo_cuota,
      retencion_porcentaje: line.retencion_porcentaje,
      retencion_cuota: line.retencion_cuota,
      es_suplido: line.es_suplido,
      suplido_nif_tercero: line.suplido_nif_tercero,
    }));

    const { error: linesError } = await supabaseAdmin.from('invoice_lines').insert(newLines);

    if (linesError) {
      console.error('[API recurring] Lines insert error:', linesError);
      await supabaseAdmin.from('invoices').delete().eq('id', createdInvoice.id);
      return res.status(500).json({ error: `Error al copiar líneas: ${linesError.message}` });
    }
  }

  // Update series siguiente_numero
  await supabaseAdmin
    .from('invoice_series')
    .update({ siguiente_numero: nextSeq })
    .eq('id', parent.serie_id);

  // Update parent's next recurrence date
  const nextProximo = calculateNextDate(config.proximo, config.intervalo);
  const updatedConfig = {
    ...config,
    proximo: nextProximo,
  };

  await supabaseAdmin
    .from('invoices')
    .update({
      recurrente_config: updatedConfig,
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoice_id);

  // Return new invoice with lines
  const { data: lines } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', createdInvoice.id)
    .order('orden', { ascending: true });

  return res.status(201).json({
    success: true,
    data: { ...createdInvoice, lines: lines ?? [] },
    generatedFrom: invoice_id,
    nextRecurrence: nextProximo,
  });
}
